import { DocumentAPI } from './API'
import { DocumentTypeDTO } from './dtos/document-type'
import DocumentType from './DocumentType'
import { collection } from 'infra/collection'
import { PrimeProps } from 'types/definitions'
import DataLoader from 'dataloader'

export type GetDocumentType = (
  id: string | undefined,
) => DocumentType | undefined

export type FetchDocumentTypes = () => Promise<DocumentType[]>

export type PrimeDocumentTypeByShortId = (
  shortId: string,
  name?: string,
) => DocumentType | undefined

export type PrimeDocumentType = (
  props: PrimeProps<DocumentTypeDTO, 'id'>,
) => DocumentType | undefined

export default class DocumentTypeStore {
  types = collection<DocumentTypeDTO, DocumentType>({
    create: () => new DocumentType(),
  })

  constructor(private api: DocumentAPI) {}

  /**
   * Data loader
   */
  documentTypeLoader = new DataLoader(this._fetchManyTypes.bind(this))

  primeDocumentTypeByShortId: PrimeDocumentTypeByShortId = (
    short_id?: string,
    name?: string,
  ): DocumentType | undefined => {
    if (!short_id) {
      return undefined
    }

    const existing = this.types.find((x) => x.shortId === short_id)

    if (!existing) {
      const created = this.types.create({ short_id, name })

      this.fetchDocumentType(short_id).then()

      return created
    }

    if (name && !existing.name) {
      existing.set({ name })
    }

    this.fetchDocumentType(short_id).then()

    return existing
  }

  async fetchDocumentType(id: string): Promise<DocumentType | null> {
    return (await this.documentTypeLoader.load(id)) ?? null
  }

  private async _fetchManyTypes(ids: readonly string[]) {
    const dtos = await this.api.types.getMany({ ids })
    return dtos.map((dto) => (dto ? this._setType(dto) : null))
  }

  async fetchTypes(): Promise<DocumentType[]> {
    const response = await this.api.types.find()

    return response.map((type) => {
      const model = this._setType(type)

      this.documentTypeLoader.prime(type.short_id, model)

      return model
    })
  }

  /**
   * Sets the document type into the collection, merging with an
   * existing model by short id.
   */
  private _setType(dto: DocumentTypeDTO) {
    const existing = this.types.find((d) => d.shortId === dto.short_id)
    if (existing) {
      return existing.set(dto, { parse: true, stripUndefined: true })
    }
    return this.types.set(dto)
  }
}
